<template>
  <div class="card border-0 rounded-3 mb-4 bg-white shadow-sm">
    <div class="card-header bg-light-subtle d-flex justify-content-between align-items-center py-3 px-4">
      <div class="d-flex align-items-center gap-2">
        <span class="material-symbols-outlined text-primary fs-22">smart_toy</span>
        <h5 class="mb-0 fs-15 fw-bold text-dark">Protokol Uji Deteksi AI (SOP RBN/SOP-RC/AI/001)</h5>
      </div>
      <span v-if="protocol" class="badge bg-success-subtle text-success fs-12 px-3 py-2">
        {{ protocol.protocol_number }}
      </span>
      <span v-else class="badge bg-warning-subtle text-warning fs-12 px-3 py-2">
        Belum Dibuat
      </span>
    </div>

    <div class="card-body p-4">
      <!-- Existing Protocol Overview -->
      <div v-if="protocol" class="mb-4 p-3 rounded-3 bg-light border border-light-subtle">
        <div class="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
          <div>
            <div class="text-muted fs-12">Nomor Protokol Resmi</div>
            <h5 class="fw-bold text-primary mb-0">{{ protocol.protocol_number }}</h5>
          </div>
          <div class="d-flex gap-2">
            <button @click="generatePdf" :disabled="generatingPdf" class="btn btn-sm btn-outline-primary d-flex align-items-center gap-1">
              <span v-if="generatingPdf" class="spinner-border spinner-border-sm"></span>
              <span class="material-symbols-outlined fs-16">picture_as_pdf</span>
              <span>{{ generatingPdf ? 'Membuat PDF...' : 'Regenerate PDF Protokol' }}</span>
            </button>
            <a v-if="order.generated_protocol_pdf" :href="getFileUrl(order.generated_protocol_pdf)" target="_blank" class="btn btn-sm btn-success d-flex align-items-center gap-1">
              <span class="material-symbols-outlined fs-16">download</span>
              <span>Unduh Dokumen Resmi A4</span>
            </a>
            <a v-if="protocol.verification?.verification_code" :href="getVerificationUrl(protocol.verification.verification_code)" target="_blank" class="btn btn-sm btn-info text-white d-flex align-items-center gap-1">
              <span class="material-symbols-outlined fs-16">verified</span>
              <span>Cek Verifikasi Publik</span>
            </a>
          </div>
        </div>

        <div class="row g-2 fs-13">
          <div class="col-md-3">
            <span class="text-muted">Skor Deteksi AI:</span>
            <span class="fw-bold text-dark ms-1">{{ protocol.ai_score_initial }}% &rarr; {{ protocol.ai_score_final }}%</span>
          </div>
          <div class="col-md-3">
            <span class="text-muted">Zona Deteksi:</span>
            <span class="badge ms-1" :class="protocol.ai_score_final <= protocol.applied_threshold ? 'bg-success' : 'bg-warning'">
              {{ protocol.internal_zone }}
            </span>
          </div>
          <div class="col-md-3">
            <span class="text-muted">Status:</span>
            <span class="badge ms-1" :class="protocol.status_code === 'PASS' ? 'bg-success' : 'bg-danger'">{{ protocol.status_code }}</span>
          </div>
          <div class="col-md-3">
            <span class="text-muted">Total Run Siklus:</span>
            <span class="fw-bold text-dark ms-1">{{ protocol.total_runs_count }} Run</span>
          </div>
        </div>
      </div>

      <form @submit.prevent="saveProtocol">
        <!-- Detector & Language Scope -->
        <div class="row mb-3">
          <div class="col-md-4 mb-2">
            <label class="form-label fs-13 fw-semibold">Perangkat AI Detector <span class="text-danger">*</span></label>
            <input type="text" class="form-control form-control-sm" v-model="form.detector_name" required placeholder="Turnitin AI / GPTZero" />
          </div>
          <div class="col-md-4 mb-2">
            <label class="form-label fs-13 fw-semibold">Versi Perangkat</label>
            <input type="text" class="form-control form-control-sm" v-model="form.detector_version" placeholder="2026 Model Release" />
          </div>
          <div class="col-md-4 mb-2">
            <label class="form-label fs-13 fw-semibold">Bahasa Naskah <span class="text-danger">*</span></label>
            <input type="text" class="form-control form-control-sm" v-model="form.document_language" required placeholder="Bahasa Indonesia / English" />
          </div>
        </div>

        <div class="row mb-3">
          <div class="col-md-6 mb-2">
            <label class="form-label fs-13 fw-semibold">Lingkup Teks Dianalisis</label>
            <input type="text" class="form-control form-control-sm" v-model="form.analyzed_scope" required placeholder="Seluruh bab naskah utama (abstrak s.d. kesimpulan)" />
          </div>
          <div class="col-md-6 mb-2">
            <label class="form-label fs-13 fw-semibold">Lingkup Dikecualikan</label>
            <input type="text" class="form-control form-control-sm" v-model="form.excluded_scope" placeholder="Daftar pustaka, lampiran, ucapan terima kasih" />
          </div>
        </div>

        <!-- Ambang Batas, Skor & Zona -->
        <div class="p-3 bg-light rounded-2 mb-3 border">
          <div class="fw-bold fs-13 mb-2 text-dark">Ambang Batas & Pengukuran Skor AI (SOP RBN)</div>
          <div class="row">
            <div class="col-md-3 mb-2">
              <label class="form-label fs-12">Ambang Batas Maks. (%)</label>
              <input type="number" step="0.1" class="form-control form-control-sm" v-model="form.applied_threshold" @input="updateZone" required />
            </div>
            <div class="col-md-3 mb-2">
              <label class="form-label fs-12">Skor AI Awal / Run 1 (%) <span class="text-danger">*</span></label>
              <input type="number" step="0.1" class="form-control form-control-sm" v-model="form.ai_score_initial" required />
            </div>
            <div class="col-md-3 mb-2">
              <label class="form-label fs-12">Skor AI Akhir (%) <span class="text-danger">*</span></label>
              <input type="number" step="0.1" class="form-control form-control-sm" v-model="form.ai_score_final" @input="updateZone" required />
            </div>
            <div class="col-md-3 mb-2">
              <label class="form-label fs-12">Zona Internal RBN</label>
              <select class="form-select form-select-sm" v-model="form.internal_zone" required>
                <option value="Rendah (0-20%)">Rendah (0-20%)</option>
                <option value="Dalam Batas (21-40%)">Dalam Batas (21-40%)</option>
                <option value="Melebihi Batas (41-60%)">Melebihi Batas (41-60%)</option>
                <option value="Signifikan (>60%)">Signifikan (>60%)</option>
              </select>
            </div>
            <div class="col-md-6 mb-2">
              <label class="form-label fs-12">Status Kode Keputusan <span class="text-danger">*</span></label>
              <select class="form-select form-select-sm" v-model="form.status_code" required>
                <option value="PASS">PASS (Memenuhi Syarat)</option>
                <option value="REVISE">REVISE (Perlu Revisi Penulis/Editor)</option>
                <option value="CLARIFY">CLARIFY (Perlu Klarifikasi)</option>
                <option value="ESCALATE">ESCALATE (Eskalasi ke Head QC)</option>
              </select>
            </div>
            <div class="col-md-6 mb-2">
              <label class="form-label fs-12">Total Siklus Run</label>
              <input type="number" class="form-control form-control-sm" v-model="form.total_runs_count" min="1" />
            </div>
          </div>
        </div>

        <!-- Section Reviews -->
        <div class="p-3 bg-light-subtle rounded-2 mb-3 border">
          <div class="d-flex justify-content-between align-items-center mb-2">
            <div class="fw-bold fs-13 text-dark">Matriks Review Per Bab / Bagian Naskah</div>
            <button type="button" @click="addSectionReview" class="btn btn-xs btn-outline-primary btn-sm fs-11">
              + Tambah Bab
            </button>
          </div>
          
          <div v-for="(sec, idx) in form.section_reviews" :key="idx" class="row g-2 mb-2 p-2 bg-white rounded border align-items-center">
            <div class="col-md-3">
              <input type="text" class="form-control form-control-sm fs-12" v-model="sec.section_name" placeholder="Bab / Bagian..." />
            </div>
            <div class="col-md-2">
              <input type="text" class="form-control form-control-sm fs-12" v-model="sec.page_range" placeholder="Hlm 1-5" />
            </div>
            <div class="col-md-2">
              <input type="text" class="form-control form-control-sm fs-12" v-model="sec.indicator_score" placeholder="Indikator AI..." />
            </div>
            <div class="col-md-3">
              <input type="text" class="form-control form-control-sm fs-12" v-model="sec.context_note" placeholder="Catatan konteks..." />
            </div>
            <div class="col-md-2 d-flex align-items-center gap-1">
              <select class="form-select form-select-sm fs-12" v-model="sec.action">
                <option value="No action">No action</option>
                <option value="Review">Review</option>
                <option value="Revise">Revise</option>
                <option value="Clarify">Clarify</option>
              </select>
              <button type="button" @click="removeSectionReview(idx)" class="btn btn-link text-danger p-0">
                <span class="material-symbols-outlined fs-18">delete</span>
              </button>
            </div>
          </div>
        </div>

        <!-- Masked Metadata & Disclaimer -->
        <div class="row mb-3">
          <div class="col-md-6 mb-2">
            <label class="form-label fs-13 fw-semibold">Nama Penulis (Masked)</label>
            <input type="text" class="form-control form-control-sm" v-model="form.author_name_masked" required />
          </div>
          <div class="col-md-6 mb-2">
            <label class="form-label fs-13 fw-semibold">Judul Naskah (Masked)</label>
            <input type="text" class="form-control form-control-sm" v-model="form.document_title_masked" required />
          </div>

          <!-- Examiner & Signatory Details -->
          <div class="col-12 mt-2">
            <div class="card bg-light-subtle border p-3 rounded-3 mb-2">
              <h6 class="fs-13 fw-bold text-dark mb-2 d-flex align-items-center gap-1">
                <span class="material-symbols-outlined fs-18 text-primary">draw</span>
                <span>Pemeriksa & Penandatangan Pengesahan Protokol</span>
              </h6>
              <div class="row g-3">
                <div class="col-md-4">
                  <label class="form-label fs-12 fw-semibold">Nama Examiner AI <span class="text-danger">*</span></label>
                  <input type="text" class="form-control form-control-sm" v-model="form.examiner_name" required />
                </div>
                <div class="col-md-4">
                  <label class="form-label fs-12 fw-semibold">Pilih Penandatangan Terdaftar</label>
                  <select class="form-select form-select-sm" v-model="form.signatory_id" @change="onSignatoryChange">
                    <option :value="null">-- Gunakan Penandatangan Default --</option>
                    <option v-for="sig in signatories" :key="sig.id" :value="sig.id">
                      {{ sig.name }} ({{ sig.position }}) {{ sig.is_default ? '★ Default' : '' }}
                    </option>
                  </select>
                </div>
                <div class="col-md-4">
                  <label class="form-label fs-12 fw-semibold">Nama & Jabatan Pengesahan</label>
                  <input type="text" class="form-control form-control-sm mb-1" v-model="form.signatory_name" placeholder="Nama penandatangan" />
                  <input type="text" class="form-control form-control-sm" v-model="form.signatory_position" placeholder="Jabatan penandatangan" />
                </div>
                <div v-if="selectedSignatoryPreviewUrl" class="col-12 mt-1">
                  <div class="d-flex align-items-center gap-3 p-2 bg-white rounded border">
                    <span class="fs-11 text-muted fw-semibold">Preview Tanda Tangan:</span>
                    <img :src="selectedSignatoryPreviewUrl" alt="Tanda Tangan" style="height: 32px; max-width: 120px; object-fit: contain;" />
                    <span class="fs-11 text-success d-flex align-items-center gap-1">
                      <span class="material-symbols-outlined fs-14">check_circle</span>
                      <span>Siap disematkan pada sertifikat protokol</span>
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="col-12 mt-2">
            <div class="form-check">
              <input class="form-check-input" type="checkbox" v-model="form.disclaimer_agreed" id="discCheck" required>
              <label class="form-check-label fs-12 text-secondary" for="discCheck">
                Saya menyetujui Disclaimer Akademik Resmi RBN bahwa deteksi AI bersifat probabilistik dan telah diverifikasi secara manual.
              </label>
            </div>
          </div>
        </div>

        <div class="d-flex justify-content-end gap-2 mt-4">
          <button type="submit" class="btn btn-primary px-4 py-2 d-flex align-items-center gap-2" :disabled="submitting">
            <span v-if="submitting" class="spinner-border spinner-border-sm"></span>
            <span class="material-symbols-outlined fs-18">save</span>
            <span>{{ protocol ? 'Perbarui Protokol' : 'Simpan & Terbitkan Protokol RC-AI' }}</span>
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, reactive, onMounted, computed } from 'vue';
import api from '../../../services/api';
import protocolSignatoryService from '../../../services/protocolSignatoryService';

export default defineComponent({
  name: 'AiDetectorProtocolForm',
  props: {
    order: {
      type: Object,
      required: true
    }
  },
  emits: ['refresh'],
  setup(props, { emit }) {
    const protocol = computed(() => props.order?.protocol_ai_detector || null);
    const signatories = ref<any[]>([]);
    const submitting = ref(false);
    const generatingPdf = ref(false);

    const form = reactive({
      detector_name: 'Turnitin AI Writing Detection',
      detector_version: 'v2026.1',
      report_submission_id: 'AI-' + Date.now(),
      document_language: 'Bahasa Indonesia',
      analyzed_scope: 'Seluruh teks artikel utama (Abstrak s.d. Kesimpulan)',
      excluded_scope: 'Daftar pustaka & lampiran',
      applied_threshold: 40,
      ai_score_initial: 48,
      ai_score_final: 28,
      total_runs_count: 1,
      status_code: 'PASS',
      internal_zone: 'Dalam Batas (21-40%)',
      examiner_name: 'Staff Analis AI RBN',
      qc_name: 'Tim QC RBN',
      service_manager_name: 'Manajer Layanan RBN',
      director_name: 'Direktur RBN',
      signatory_id: null as number | null,
      signatory_name: '',
      signatory_position: '',
      signatory_image: null as string | null,
      disclaimer_agreed: true,
      author_name_masked: maskName(props.order?.customer?.name || 'Customer RBN'),
      document_title_masked: 'Naskah ' + (props.order?.order_code || ''),
      institution: 'Institusi Terdaftar',
      runs: [
        {
          run_number: 1,
          submitted_by_role: 'EXAMINER',
          manuscript_file: props.order?.ai_check_detail?.source_file || 'initial_file.docx',
          sha256_hash: Array.from({length: 64}, () => Math.floor(Math.random()*16).toString(16)).join(''),
          test_date: new Date().toISOString().substring(0, 10),
          score: 28,
          status: 'PASS',
          examiner_notes: 'Pengujian awal oleh tim RBN.'
        }
      ],
      section_reviews: [
        {
          section_name: 'Pendahuluan & Tinjauan Pustaka',
          page_range: 'Hlm 1-3',
          indicator_score: '15%',
          context_note: 'Proporsi wajar sintesis literatur.',
          action: 'No action'
        },
        {
          section_name: 'Pembahasan & Interpretasi',
          page_range: 'Hlm 4-8',
          indicator_score: '28%',
          context_note: 'Telah diparafrase manual untuk mereduksi pola generatif.',
          action: 'No action'
        }
      ]
    });

    function maskName(str: string): string {
      if (!str || str.length <= 2) return str;
      const parts = str.split(' ');
      return parts.map(p => p[0] + '***' + (p.length > 3 ? p[p.length - 1] : '')).join(' ');
    }

    const updateZone = () => {
      const score = Number(form.ai_score_final);
      if (score <= 20) {
        form.internal_zone = 'Rendah (0-20%)';
        form.status_code = 'PASS';
      } else if (score <= 40) {
        form.internal_zone = 'Dalam Batas (21-40%)';
        form.status_code = 'PASS';
      } else if (score <= 60) {
        form.internal_zone = 'Melebihi Batas (41-60%)';
        form.status_code = 'REVISE';
      } else {
        form.internal_zone = 'Signifikan (>60%)';
        form.status_code = 'REVISE';
      }
    };

    const addSectionReview = () => {
      form.section_reviews.push({
        section_name: '',
        page_range: '',
        indicator_score: '',
        context_note: '',
        action: 'No action'
      });
    };

    const removeSectionReview = (idx: number) => {
      form.section_reviews.splice(idx, 1);
    };

    const saveProtocol = async () => {
      submitting.value = true;
      try {
        if (protocol.value) {
          await api.put(`/protocol-ai-detectors/${protocol.value.id}`, form);
          alert('Protokol berhasil diperbarui.');
        } else {
          await api.post(`/orders/${props.order.order_id}/protocol-ai-detector`, form);
          alert('Protokol RC-AI berhasil diterbitkan!');
        }
        emit('refresh');
      } catch (err: any) {
        console.error('Error saving AI detector protocol:', err);
        alert(err.response?.data?.message || 'Gagal menyimpan protokol.');
      } finally {
        submitting.value = false;
      }
    };

    const generatePdf = async () => {
      generatingPdf.value = true;
      try {
        await api.post(`/orders/${props.order.order_id}/generate-protocol-pdf`);
        alert('PDF Protokol resmi berhasil digenerate.');
        emit('refresh');
      } catch (err: any) {
        console.error('Error generating PDF:', err);
        alert(err.response?.data?.message || 'Gagal generate PDF.');
      } finally {
        generatingPdf.value = false;
      }
    };

    const getFileUrl = (path?: string | null) => {
      if (!path) return '';
      if (path.startsWith('blob:') || path.startsWith('data:')) return path;
      if (path.includes('/storage/')) {
        const cleanPath = path.replace(/^https?:\/\/[^/]+\/storage\//, '').replace(/^storage\//, '').replace(/^\//, '');
        const baseUrl = process.env.NODE_ENV === 'production'
          ? 'https://backend-beta.rayacheck.id'
          : 'http://jurnal-backend.test';
        return `${baseUrl}/storage/${cleanPath}`;
      }
      if (path.startsWith('http://') || path.startsWith('https://')) {
        return path;
      }
      const cleanPath = path.replace(/^\//, '');
      const baseUrl = process.env.NODE_ENV === 'production'
        ? 'https://backend-beta.rayacheck.id'
        : 'http://jurnal-backend.test';
      return `${baseUrl}/storage/${cleanPath}`;
    };

    const getVerificationUrl = (code: string) => {
      const baseUrl = window.location.origin;
      return `${baseUrl}/verify/${code}`;
    };

    const loadSignatories = async () => {
      try {
        const res = await protocolSignatoryService.getActive();
        signatories.value = res.data.data || [];
        if (!form.signatory_id && !protocol.value?.signatory_id) {
          const def = signatories.value.find((s: any) => s.is_default) || signatories.value[0];
          if (def) {
            form.signatory_id = def.id;
            form.signatory_name = def.name;
            form.signatory_position = def.position;
            form.signatory_image = def.signature_image;
            form.director_name = def.name;
          }
        }
      } catch (err) {
        console.error('Failed to load active signatories:', err);
      }
    };

    const onSignatoryChange = () => {
      const chosen = signatories.value.find((s: any) => s.id === form.signatory_id);
      if (chosen) {
        form.signatory_name = chosen.name;
        form.signatory_position = chosen.position;
        form.signatory_image = chosen.signature_image;
        form.director_name = chosen.name;
      }
    };

    const selectedSignatoryPreviewUrl = computed(() => {
      let raw = '';
      if (form.signatory_id) {
        const chosen = signatories.value.find((s: any) => s.id === form.signatory_id);
        if (chosen) raw = chosen.signature_image_url || chosen.signature_image || '';
      } else if (protocol.value?.signatory_image_url || protocol.value?.signatory_image) {
        raw = protocol.value.signatory_image_url || protocol.value.signatory_image || '';
      } else {
        const def = signatories.value.find((s: any) => s.is_default);
        raw = def?.signature_image_url || def?.signature_image || '';
      }
      return raw ? getFileUrl(raw) : null;
    });

    onMounted(async () => {
      await loadSignatories();
      if (protocol.value) {
        Object.assign(form, protocol.value);
      }
    });

    return {
      protocol,
      signatories,
      onSignatoryChange,
      selectedSignatoryPreviewUrl,
      form,
      submitting,
      generatingPdf,
      updateZone,
      addSectionReview,
      removeSectionReview,
      saveProtocol,
      generatePdf,
      getFileUrl,
      getVerificationUrl
    };
  }
});
</script>
